/**
 * Featured Posts Component
 * 
 * Displays featured/recent blog posts for the home page.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { BlogConfig } from '../BlogConfig';
import { getFeaturedPosts } from '../utils/postLoader';
import { useLanguage } from '../../i18n';

const FeaturedPosts = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t, language } = useLanguage();

  const loadPosts = useCallback(async () => {
    try {
      // Map app languages to locales for date formatting
      const localeMap = { en: 'en-US', pt: 'pt-BR', fr: 'fr-FR' };
      const featured = await getFeaturedPosts(BlogConfig.content.featuredPostsCount, localeMap[language]);
      setPosts(featured);
    } catch (error) {
      console.error('Error loading featured posts:', error);
    } finally {
      setLoading(false);
    }
  }, [language]);

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-white rounded-xl shadow-sm p-6 animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
            <div className="h-3 bg-gray-200 rounded w-full mb-2"></div>
            <div className="h-3 bg-gray-200 rounded w-2/3"></div>
          </div>
        ))}
      </div>
    );
  }

  if (posts.length === 0) return null;

  return (
    <section className="w-full">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{t('blog.featuredTitle')}</h2>
          <p className="text-gray-600 mt-1">{t('blog.featuredSubtitle')}</p>
        </div>
        <Link 
          to="/blog"
          className="hidden sm:flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium transition-colors"
        >
          {t('blog.viewAll')}
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
          </svg>
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {posts.map((post, index) => (
          <article 
            key={post.slug}
            className="group bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden border border-gray-100"
            style={{ animationDelay: `${index * 100}ms` }}
          >
            <Link to={`/blog/${post.slug}`} className="block p-6">
              {/* Category Badge */}
              <div className="flex items-center gap-2 mb-3">
                {(() => {
                  const categoryConfig = BlogConfig.categories.find(
                    cat => cat.id === post.category
                  ) || { label: post.category, color: '#6b7280' };
                  return (
                    <span
                      className="px-2.5 py-0.5 rounded-full text-xs font-medium"
                      style={{
                        backgroundColor: `${categoryConfig.color}15`,
                        color: categoryConfig.color,
                      }}
                    >
                      {categoryConfig.label}
                    </span>
                  );
                })()}
                {post.featured && (
                  <span className="text-amber-500 text-xs">★</span>
                )}
              </div>

              {/* Title */}
              <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-blue-600 transition-colors">
                {post.title}
              </h3>

              {/* Description */}
              <p className="text-gray-600 text-sm line-clamp-2 mb-4">
                {post.description}
              </p>

              {/* Meta */}
              <div className="flex items-center gap-3 text-xs text-gray-500">
                <span>{post.formattedDate}</span>
                <span>·</span>
                <span>{post.readingTime} {t('blog.minuteRead')}</span>
              </div>
            </Link>
          </article>
        ))}
      </div>

      {/* Mobile View All Button */}
      <div className="mt-8 text-center sm:hidden">
        <Link 
          to="/blog"
          className="inline-flex items-center gap-2 px-6 py-3 bg-gray-900 text-white rounded-lg font-medium hover:bg-gray-800 transition-colors"
        >
          {t('blog.viewAllArticles')}
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
          </svg>
        </Link>
      </div>
    </section>
  );
};

export default FeaturedPosts;
